import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Plane, Menu, X, Heart, BarChart3, Wallet } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import NotificationBell from "./NotificationBell";

const navLinks = [
  { label: "Search", to: "/search" },
  { label: "Explore", to: "/explore" },
  { label: "Calendar", to: "/calendar" },
  { label: "Cabins", to: "/cabin-compare" },
  { label: "Tracker", to: "/tracker" },
  { label: "Alerts", to: "/alerts" },
  { label: "Assistant", to: "/chat" },
  { label: "Pricing", to: "/pricing" },
];

function DesktopAuth({ user, onLogout, pathname }) {
  if (!user) {
    return (
      <>
        <Link to="/login"><Button variant="ghost" size="sm" className="text-slate-700">Login</Button></Link>
        <Link to="/register"><Button size="sm" className="bg-blue-600 font-semibold hover:bg-blue-700">Sign Up for PRO</Button></Link>
      </>
    );
  }
  return (
    <div className="flex items-center gap-3">
      <NotificationBell />
      <Link to="/wallet" aria-label="Points wallet" data-testid="nav-wallet-link" className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${pathname === "/wallet" ? "border-blue-200 bg-blue-50 text-blue-600" : "border-slate-200 text-slate-500 hover:text-blue-600"}`}><Wallet className="h-4 w-4" /></Link>
      <Link to="/wishlist" aria-label="Wishlist" data-testid="nav-wishlist-link" className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${pathname === "/wishlist" ? "border-blue-200 bg-blue-50 text-blue-600" : "border-slate-200 text-slate-500 hover:text-red-500"}`}>
        <Heart className="h-4 w-4" />
      </Link>
      {user.isAdmin && <Link to="/admin/insights" data-testid="nav-insights-link" className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-bold text-slate-700 hover:border-blue-300 hover:text-blue-600"><BarChart3 className="h-4 w-4" /> Insights</Link>}
      {user.pro && <span className="rounded-md bg-blue-600 px-2 py-0.5 text-xs font-bold text-white">PRO</span>}
      <span className="text-sm font-medium text-slate-700">{user.name || user.email}</span>
      <Button variant="outline" size="sm" onClick={onLogout}>Logout</Button>
    </div>
  );
}

function MobileMenu({ user, onLogout }) {
  return (
    <div className="border-t border-slate-200 bg-white lg:hidden">
      <nav className="mx-auto flex max-w-7xl flex-col px-5 py-3">
        {navLinks.map((l) => (
          <Link key={l.to} to={l.to} className="rounded-md px-3 py-3 text-base font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100">{l.label}</Link>
        ))}
        <div className="mt-3 flex flex-col gap-2 border-t border-slate-100 pt-3">
          {user ? (
            <>
              <Link to="/wishlist"><Button variant="outline" className="w-full gap-2"><Heart className="h-4 w-4" /> Wishlist</Button></Link>
              <Link to="/wallet"><Button variant="outline" className="w-full gap-2"><Wallet className="h-4 w-4" /> Points Wallet</Button></Link>
              {user.isAdmin && <Link to="/admin/insights"><Button variant="outline" className="w-full gap-2"><BarChart3 className="h-4 w-4" /> Insights</Button></Link>}
              <Button variant="outline" onClick={onLogout}>Logout</Button>
            </>
          ) : (
            <>
              <Link to="/login"><Button variant="outline" className="w-full">Login</Button></Link>
              <Link to="/register"><Button className="w-full bg-blue-600 hover:bg-blue-700">Sign Up for PRO</Button></Link>
            </>
          )}
        </div>
      </nav>
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const handleLogout = () => { logout(); navigate("/"); };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5">
        <Link to="/" className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600 text-white"><Plane className="h-4 w-4" strokeWidth={2.5} /></span>
          <span className="text-lg font-extrabold tracking-tight text-slate-900">FindMyFlight</span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navLinks.map((l) => (
            <Link key={l.to} to={l.to} className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${location.pathname === l.to ? "text-blue-600" : "text-slate-600 hover:text-slate-900"}`}>{l.label}</Link>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <DesktopAuth user={user} onLogout={handleLogout} pathname={location.pathname} />
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <NotificationBell />
          <button onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="mobile-menu-toggle" className="flex h-11 w-11 items-center justify-center rounded-lg text-slate-700 active:bg-slate-100">{open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}</button>
        </div>
      </div>
      {open && <MobileMenu user={user} onLogout={handleLogout} />}
    </header>
  );
}
